/**
 * Signatures for automated traffic that OpenPanel currently records as real
 * sessions (HB-957).
 *
 * Prod-quantified: Chrome Headless alone fires 13,156 events across 3,599
 * sessions and blank-UA agents 1,764 events across just 22 sessions, which
 * distorts entry points, path lengths and exit pages on every journey metric.
 */
const BOT_USER_AGENT_PATTERNS = [
	// Headless browsers — the single largest contributor.
	/headless/i,
	/phantomjs/i,
	/electron/i,
	// Automation drivers.
	/puppeteer/i,
	/playwright/i,
	/selenium/i,
	/webdriver/i,
	/cypress/i,
	// Named scrapers and email/link scanners seen in prod.
	/blazer/i,
	/\bbot\b/i,
	/crawler/i,
	/spider/i,
	/scrap(er|y)/i,
	/slurp/i,
	/curl\//i,
	/wget/i,
	/python-requests/i,
	/axios\//i,
	/go-http-client/i,
	/java\//i,
	/okhttp/i,
	/apache-httpclient/i,
	// Link-unfurling / preview fetchers that inflate single-hit sessions.
	/preview/i,
	/fetcher/i,
	/monitor/i,
	/pingdom/i,
	/lighthouse/i,
	/gtmetrix/i,
	/pagespeed/i
];

/**
 * True when the current client looks automated rather than human.
 *
 * Three first-pass rules, matching the HB-957 acceptance criteria:
 *  1. `navigator.webdriver` — the standards-track automation flag, set by
 *     Puppeteer/Playwright/Selenium even when the UA is spoofed to look real.
 *  2. A blank/missing user agent — no legitimate browser omits it, and prod
 *     shows these averaging ~80 events per session.
 *  3. A known headless / scraper / scanner UA signature.
 *
 * Server-side this returns `false`: SSR has no navigator, and suppressing there
 * would drop the render itself rather than an event. Detection is deliberately
 * signature-based and conservative — a false positive silently deletes real
 * traffic, which is worse than letting a stray bot through.
 */
export const isBotTraffic = (): boolean => {
	if (typeof navigator === 'undefined') {
		return false;
	}

	if (navigator.webdriver) {
		return true;
	}

	const userAgent = navigator.userAgent?.trim();

	if (!userAgent) {
		return true;
	}

	return BOT_USER_AGENT_PATTERNS.some((pattern) => pattern.test(userAgent));
};
